// src/oura/components/site/HealthByAreaCard.tsx
//
// Health by area: how much of the site the crawl read, and the four areas the
// backend measures from the same crawl, with a line on the card's foot saying
// what the numbers are shares of. Rendered only when the crawl ran; on a
// failed crawl the backend sends zeros nobody measured.

import { ListChecksIcon } from "lucide-react";
import type { BusinessAudit } from "../../audit";
import { cn } from "../../lib/cn";
import { Dot, FoldCard, ListRow, TYPE, type Tone } from "@oura/ui";

type Row = { key: string; label: string; value: string; tone: Tone; sub?: string };

/**
 * The four areas, read and never derived. These used to be invented on this
 * screen: the two percentages were `siteHealth ± a constant` and Markup /
 * robots.txt were the literals "Missing" and "Not found", so a site with full
 * schema markup and a valid robots.txt was told it had neither. The BE
 * computes all four from the crawl; read them, or say "Not measured".
 */
function areaRows(hba: BusinessAudit["healthByArea"]): Row[] {
    if (!hba) {
        return ["Crawlability", "Internal linking", "Markup", "robots.txt"].map((label) => ({
            key: label,
            label,
            value: "Not measured",
            tone: "gray",
        }));
    }
    const pct = (v: number): Tone => (v < 80 ? "rose" : "green");
    return [
        { key: "crawl", label: "Crawlability", value: `${hba.crawlability}%`, tone: pct(hba.crawlability) },
        { key: "links", label: "Internal linking", value: `${hba.internalLinking}%`, tone: pct(hba.internalLinking) },
        { key: "markup", label: "Markup", value: hba.markup === "present" ? "Present" : "Missing", tone: hba.markup === "present" ? "green" : "rose" },
        // Found means the file exists. Oura does not test what any AI reader
        // does with it.
        { key: "robots", label: "robots.txt", value: hba.robotsTxt === "found" ? "Found" : "Not found", tone: hba.robotsTxt === "found" ? "green" : "rose" },
    ];
}

export function HealthByAreaCard({
    healthByArea,
    pagesCrawled,
    pagesKnown,
    truncationReason,
    crawlDay,
    busy,
}: {
    healthByArea: BusinessAudit["healthByArea"];
    pagesCrawled: number;
    /** The crawl's stable denominator; null on fixtures and older scans. */
    pagesKnown?: number | null;
    /** Which limit stopped a crawl that did not read every known page. */
    truncationReason?: BusinessAudit["truncationReason"];
    crawlDay: string | null;
    busy: boolean;
}) {
    const cut = typeof pagesKnown === "number" && pagesKnown > pagesCrawled;
    // The card renders only for a crawl that ran, so 0 is a payload that
    // never sent the count, not a crawl that read nothing.
    const counted = pagesCrawled > 0;
    const pagesRow: Row = {
        key: "pages",
        label: "Pages crawled",
        value: !counted
            ? "Not measured"
            : typeof pagesKnown === "number" && pagesKnown > 0 ? `${pagesCrawled} of ${pagesKnown}` : `${pagesCrawled}`,
        // A crawl that stops at its ceiling is by design, not a fault: amber,
        // and the row says which limit it was.
        tone: !counted ? "gray" : cut ? "amber" : "green",
        sub: cut
            ? truncationReason === "time_budget"
                ? "The crawl stopped at its time budget."
                : truncationReason === "max_pages"
                    ? "The crawl stopped at its page limit."
                    : undefined
            : undefined,
    };
    const rows = [pagesRow, ...areaRows(healthByArea)];

    return (
        <FoldCard
            id="health-by-area"
            title="Health by area"
            icon={{ icon: ListChecksIcon, area: "site" }}
            right={crawlDay ? `${crawlDay} crawl` : undefined}
            foldedFact={healthByArea ? undefined : "Not measured"}
        >
            {/* A column that fills the card, so the note sits on the card's foot
                beside a taller Strengths and weaknesses card. */}
            <div aria-busy={busy} className={cn("flex h-full flex-col transition-opacity", busy && "opacity-50")}>
                {rows.map((r) => (
                    <ListRow
                        key={r.key}
                        icon={<Dot tone={r.tone} />}
                        title={<span className="text-gray-600">{r.label}</span>}
                        sub={r.sub}
                        right={
                            <span className={cn(TYPE.tb, "shrink-0 whitespace-nowrap tabular-nums", r.tone === "gray" ? "font-medium text-gray-400" : "font-semibold text-gray-900")}>
                                {r.value}
                            </span>
                        }
                    />
                ))}
                {healthByArea && counted && (
                    <p className={cn(TYPE.tm, "m-0 mt-auto pt-3 text-gray-400")}>
                        Crawlability and internal linking are shares of the {pagesCrawled} {pagesCrawled === 1 ? "page" : "pages"} read.
                        {/* Rule 4: Oura only checks the file exists. */}
                        {" "}robots.txt is checked for, not tested with any AI reader.
                    </p>
                )}
            </div>
        </FoldCard>
    );
}
